/**
 * Model Enable/Disable Configuration
 * 
 * Controls which models are available to users. Models can be:
 * - enabled: Ready for use
 * - disabled: Temporarily unavailable or no API key configured
 * - future: Planned model, not yet integrated (Coming Soon)
 * - off: Hidden from the UI entirely
 */

import { AIProvider, AIModelConfig } from '../AIProvider.types';
import { ALL_MODELS } from './modelConfig';

/**
 * Model availability status
 */
export type ModelStatus = 'enabled' | 'disabled' | 'future' | 'off';

/**
 * Configuration mapping model IDs to their status
 */
export interface ModelEnableConfig {
  [modelId: string]: ModelStatus;
}

/**
 * Model enable/disable configuration
 * 
 * Update this object to control which models are available:
 * - 'enabled': Model is available for selection
 * - 'disabled': Model is grayed out with "Disabled" badge
 * - 'future': Model is grayed out with "Coming Soon" badge
 * - 'off': Model is hidden from the UI
 */
export const MODEL_ENABLE_CONFIG: ModelEnableConfig = {
  // OpenAI
  'gpt-4o': 'enabled',
  'gpt-4-1106-preview': 'off',
  'gpt-5-nano': 'off',
  'gpt-5-mini': 'enabled',
  'gpt-5.2': 'enabled',
  
  // Anthropic
  'claude-opus-4.5': 'enabled',
  'claude-sonnet-4.5': 'enabled',
  'claude-haiku-4.5': 'enabled',
  
  // Google
  'gemini-2.5-flash': 'enabled',
  'gemini-2.5-pro': 'enabled',
  'gemini-3-flash-preview': 'enabled',
  'gemini-3-pro-preview': 'enabled',
  
  // xAI
  'grok-3-mini': 'enabled',
  'grok-4-0709': 'enabled',
  'grok-4-1-fast': 'enabled'
};

/**
 * Get the status of a specific model
 * @param modelId - The model ID to check
 * @returns The model status, defaults to 'disabled' if not found
 */
export const getModelStatus = (modelId: string): ModelStatus => {
  return MODEL_ENABLE_CONFIG[modelId] ?? 'disabled';
};

/**
 * Check if a model is selectable (only 'enabled' models can be selected)
 * @param modelId - The model ID to check
 * @returns Whether the model can be selected
 */
export const isModelSelectable = (modelId: string): boolean => {
  return getModelStatus(modelId) === 'enabled';
};

/**
 * Check if a model should be visible in the UI
 * All configured models are visible, just with different styling
 * @param modelId - The model ID to check
 * @returns Whether the model should be displayed
 */
export const isModelVisible = (modelId: string): boolean => {
  return modelId in MODEL_ENABLE_CONFIG && MODEL_ENABLE_CONFIG[modelId] !== 'off';
};

/**
 * Get all enabled model IDs
 * @returns Array of model IDs that are enabled
 */
export const getEnabledModels = (): string[] => {
  return Object.entries(MODEL_ENABLE_CONFIG)
    .filter(([, status]) => status === 'enabled')
    .map(([modelId]) => modelId);
};

/**
 * Get all model IDs with a specific status
 * @param status - The status to filter by
 * @returns Array of model IDs with the given status
 */
export const getModelsByStatus = (status: ModelStatus): string[] => {
  return Object.entries(MODEL_ENABLE_CONFIG)
    .filter(([, s]) => s === status)
    .map(([modelId]) => modelId);
};

/**
 * Extended model config with status for UI display
 */
export interface AIModelConfigWithStatus extends AIModelConfig {
  status: ModelStatus;
}

/**
 * Get enabled models for a specific provider
 * @param provider - The AI provider to filter by
 * @returns Array of enabled model configurations
 */
export const getEnabledModelsByProvider = (provider: AIProvider): AIModelConfig[] => {
  return ALL_MODELS.filter(
    m => m.provider === provider && isModelSelectable(m.id)
  );
};

/**
 * Get all models for a provider with their status
 * Useful for UI display where disabled/future models should still be shown
 * @param provider - The AI provider to filter by
 * @returns Array of model configurations with status
 */
export const getAllModelsByProvider = (provider: AIProvider): AIModelConfigWithStatus[] => {
  return ALL_MODELS
    .filter(m => m.provider === provider)
    .map(m => ({
      ...m,
      status: getModelStatus(m.id)
    }))
    .filter(m => m.status !== 'off');
};

/**
 * Get all models with their status
 * @returns Array of all model configurations with status
 */
export const getAllModelsWithStatus = (): AIModelConfigWithStatus[] => {
  return ALL_MODELS
    .map(m => ({
      ...m,
      status: getModelStatus(m.id)
    }))
    .filter(m => m.status !== 'off');
};

/**
 * Get the count of enabled models per provider
 * @returns Object mapping provider to count of enabled models
 */
export const getEnabledModelCountByProvider = (): Record<AIProvider, number> => {
  const counts: Record<AIProvider, number> = {
    [AIProvider.OPENAI]: 0,
    [AIProvider.ANTHROPIC]: 0,
    [AIProvider.GOOGLE]: 0,
    [AIProvider.XAI]: 0
  };
  
  ALL_MODELS.forEach(m => {
    if (isModelSelectable(m.id)) {
      counts[m.provider]++;
    }
  });
  
  return counts;
};
